import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams, Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Icon } from 'semantic-ui-react';
import styled from 'styled-components';
import { useCritiqueGroupPublicPreview } from 'src/hooks/useCritiqueGroups';
import { BookSpine } from 'src/components/critiqueGroups/bookSpine';
import {
  CritiqueErrorState,
  CritiqueLoadingState,
} from 'src/components/critiqueGroups/critiqueStates';
import { visibilityLabel } from 'src/utils/critiqueGroupUtil';

/**
 * The LOGGED-OUT public landing for a shared critique-group link
 * (docs/features/critique-groups/public-discovery.md §16.2). Registered at the app level as a
 * plain public <Route> (NOT <Restricted>, which would bounce a signed-out
 * visitor to /signin), at `/critique-groups/:groupId/preview`.
 *
 * It shows METADATA ONLY — name, cover, linked book title, host, member count —
 * from the unauthenticated `/public-preview` endpoint, plus a "sign in / sign up
 * to request to join" CTA. It never renders content (chapters, comments, wall):
 * the endpoint doesn't return any, and joining happens only after the visitor
 * signs in and the host approves them.
 *
 * A SIGNED-IN visitor is redirected to the in-app group, which already has the
 * full authenticated preview + request-to-join flow.
 */

const Wrap = styled.div`
  max-width: 620px;
  margin: 40px auto;
  padding: 0 16px;
`;

const Hero = styled.div`
  background: #123c3a;
  color: #fff;
  border-radius: 16px;
  padding: 26px 26px 22px;
  display: flex;
  gap: 20px;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 800;
`;

const Badge = styled.span`
  font-size: 11px;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.16);
  padding: 4px 10px;
  border-radius: 999px;
  margin-left: 10px;
  white-space: nowrap;
`;

const Sub = styled.p`
  margin: 6px 0 0;
  font-size: 13.5px;
  color: #b8d6d2;
`;

const Stat = styled.div`
  margin-top: 16px;
  font-size: 13px;
  color: #cfe6e2;
`;

const Card = styled.div`
  background: var(--br-surface);
  border: 1px solid #e7e3d9;
  border-radius: 14px;
  padding: 22px 24px;
  margin-top: 18px;
`;

const CritiquePublicLanding: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { groupId } = useParams<{ groupId: string }>();
  const userProfile = useSelector((state: any) => state.user?.userProfile);
  const isSignedIn = !!userProfile?._id;

  // A signed-in visitor gets the real in-app group (full authed preview +
  // request flow). Only fetch the public projection for signed-out visitors.
  const { data: group, isLoading, isError, error, refetch } =
    useCritiqueGroupPublicPreview(groupId, { enabled: !isSignedIn });

  if (isSignedIn) {
    return <Redirect to={`/critique-groups/${groupId}`} />;
  }

  if (isLoading) return <CritiqueLoadingState />;
  if (isError || !group) {
    return (
      <Wrap>
        <CritiqueErrorState
          message={
            // 404 (private / unknown) and 403 (feature off) both read as "not
            // available to you" — don't reveal which, and don't blame the server.
            error?.status === 404 || error?.status === 403
              ? t('CritiqueNoAccess', 'This group isn’t available to you.')
              : error?.message
          }
          onRetry={() => refetch()}
        />
      </Wrap>
    );
  }

  const badge = visibilityLabel(group.visibility, t);
  const signInHref = `/signin?redirect=${encodeURIComponent(
    `/critique-groups/${groupId}`
  )}`;
  const signUpHref = `/signup?redirect=${encodeURIComponent(
    `/critique-groups/${groupId}`
  )}`;

  return (
    <Wrap data-testid="critique-public-landing">
      <Hero>
        {group.book && (
          <BookSpine
            title={group.book.title}
            coverUrl={group.coverUrl || group.book.coverUrl || undefined}
          />
        )}
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
            <Title>
              {group.name || t('CritiqueUntitledGroup', 'Untitled group')}
            </Title>
            <Badge data-testid="landing-visibility-badge">
              <Icon name={badge.icon as any} style={{ marginRight: 4 }} />
              {badge.label}
            </Badge>
          </div>
          {group.host?.displayName && (
            <Sub>
              {t('CritiqueHostedBy', 'Hosted by {{name}}', {
                name: group.host.displayName,
              })}
            </Sub>
          )}
          {group.book?.title && (
            <Sub>
              {t('CritiqueReading', 'Reading: {{title}}', {
                title: group.book.title,
              })}
            </Sub>
          )}
          <Stat>
            <Icon name="users" />{' '}
            {t('CritiqueMemberCount', '{{count}} members', {
              count: group.memberCount,
            })}
            {group.memberCap != null && ` / ${group.memberCap}`}
          </Stat>
        </div>
      </Hero>

      <Card data-testid="landing-cta">
        <p style={{ fontSize: 14, color: '#6b675e', marginBottom: 14 }}>
          {group.isFull
            ? t(
                'CritiqueGroupFullBody',
                'This group is currently full. You can’t request to join right now.'
              )
            : t(
                'CritiqueLandingJoinBody',
                'This is a critique group on BetaReader. Sign in or create a free account to request to join — the host reviews every request, and you won’t see the manuscript until they approve you.'
              )}
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Button
            color="teal"
            disabled={group.isFull}
            onClick={() => history.push(signInHref)}
            style={{ borderRadius: 9, fontWeight: 700 }}
            data-testid="landing-signin"
          >
            {t('CritiqueLandingSignIn', 'Sign in to request to join')}
          </Button>
          <Button
            basic
            color="teal"
            disabled={group.isFull}
            onClick={() => history.push(signUpHref)}
            style={{ borderRadius: 9, fontWeight: 700 }}
            data-testid="landing-signup"
          >
            {t('CritiqueLandingSignUp', 'Create a free account')}
          </Button>
        </div>
      </Card>
    </Wrap>
  );
};

export default CritiquePublicLanding;
